/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import React, { useContext, useEffect } from 'react'
import { useApplicantById } from 'src/hooks/ApplicantsSelector'
import { APPLICANT_KIND } from '@flatsy/types/Applications/Applicant'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import { Button } from '@material-ui/core'
import FlatsyButtonsField from '@flatsy/core/Form/ReactFormHooks/Field/Buttons'
import i18n from '@flatsy/utils/translations'
import { FormContext, ReturnContextProps } from '../../Context'
import { SharedProps } from '../Type'
import { useFormStyles } from '../Style'
import { hasGuarantorData } from './Type'
import { renderGuarantorType } from './Utils'

const FormHasGuarantor: React.FC<SharedProps> = ({ renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const {
    wizardContext: { context },
    saveApplicantAndContinue,
  } = useContext(FormContext) as ReturnContextProps
  const applicant = useApplicantById(context.applicantId)

  const schema = yup.object({
    has_guarantor: yup.string().required().typeError(TextError.empty),
    guarantor_kind: yup
      .string()
      .nullable()
      .when('has_guarantor', {
        is: (val: string) => val === 'true',
        then: yup.string().required().typeError(TextError.empty),
      }),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitSuccessful, isValid },
  } = useForm<hasGuarantorData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  const has_guarantor = watch('has_guarantor')

  useEffect(() => {
    if (applicant) {
      reset({
        has_guarantor:
          applicant.has_guarantor === true
            ? 'true'
            : applicant.has_guarantor === false
            ? 'false'
            : undefined,
        guarantor_kind: applicant.guarantor_kind || undefined,
      })
    }
  }, [applicant])

  const onSubmit = async (values: hasGuarantorData): Promise<undefined> => {
    if (applicant) {
      saveApplicantAndContinue(applicant, {
        ...values,
        has_guarantor: values.has_guarantor === 'true',
      })
    }
    return undefined
  }

  if (!applicant) {
    return null
  }

  return (
    <div className={formClasses.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {applicant.kind === APPLICANT_KIND.main
            ? i18n.t('do_you_have_a_guarantor')
            : i18n.t('does_your_co_signer_have_a_guarantor')}
        </LabelSeparator>
        <Controller
          name="has_guarantor"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={[
                {
                  label: i18n.t('yes'),
                  value: 'true',
                },
                {
                  label: i18n.t('no'),
                  value: 'false',
                },
              ]}
            />
          )}
        />
        {has_guarantor === 'true' && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {applicant.kind === APPLICANT_KIND.main
                ? i18n.t('who_is_your_guarantor')
                : i18n.t('who_is_his_or_her_guarantor')}
            </LabelSeparator>
            <Controller
              name="guarantor_kind"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={renderGuarantorType()}
                />
              )}
            />
          </>
        )}

        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isValid, !isValid)
        )}
      </form>
    </div>
  )
}

export default FormHasGuarantor
